import React from "react";
import { styled } from "styled-components";
import { ConfigSchema, SchemaField } from "./configTypes";
import { freshnessEnabled, freshnessFor, groupFields } from "./configModel";
import { capabilityLadder, CapabilityRung, rungLabel } from "./capability";
import { Vehicle } from "./data";
import FieldFreshness from "./FieldFreshness";
import ValueChip from "./ValueChip";
import { tokens } from "./tokens";

/**
 * The middle level of the fleet view: a row, opened in place.
 *
 * It exists for one thing the tractor page cannot do — **comparison without
 * losing your place.** Open three rows and the same fields sit at the same
 * offsets down the page, so "which of these has the newer lidar calibration" is
 * a glance. Three browser tabs are not that. Everything here is therefore laid
 * out identically every time, in schema order, whatever a given tractor happens
 * to have set.
 *
 * Read-only on purpose. Editing is a commitment with a version check and a
 * review step behind it, and that belongs on the tractor's own page where the
 * change history is visible; an edit affordance here would put a write two
 * clicks from a scan.
 *
 * What it shows: everything the row above it doesn't. Fields whose exact value
 * is already in a column are skipped rather than repeated two inches below
 * themselves.
 *
 * ## Why capability doesn't render like the rest
 *
 * As four more label-and-value lines it was the densest block on the panel and
 * the hardest to read — four near-identical rows of prose, from which you were
 * expected to re-derive the one thing you wanted, which is how far up the
 * tractor has got. It is a *ladder*, so it is drawn as one: a rail that runs
 * green up to the highest approval held and grey above it. The answer is then
 * the length of a line rather than a sentence to parse, and the qualifiers stay
 * beside their rungs where they can be read when they matter.
 */
interface RowDetailProps {
  vehicle: Vehicle;
  schema: ConfigSchema;
  /** Field names already rendered as their own column. */
  columnFields: readonly string[];
  /** Fixed at mount by the table, so staleness doesn't shift between rows. */
  now: number;
}

const RowDetail: React.FC<RowDetailProps> = ({
  vehicle,
  schema,
  columnFields,
  now,
}) => {
  const freshness = freshnessEnabled(schema);
  const ladder = capabilityLadder(schema, vehicle.attrs);
  const rungNames = new Set(ladder.rungs.map((r) => r.field.name));
  const skip = new Set(columnFields);

  // Provenance is API bookkeeping and stays on the tractor page, exactly as the
  // table excludes it. Anything else the schema grows lands here by default,
  // which is the safe direction: a new field is visible without being assumed
  // important enough for a column.
  const groups = groupFields(
    schema.fields,
    (f) => f.group !== "provenance" && !skip.has(f.name),
    schema.groups,
  );

  return (
    <Panel>
      {groups.map((group) => {
        const isCapability = group.fields.some((f) => rungNames.has(f.name));
        // The approved-route list lives in the capability group but is not a
        // rung — it has no state to be on the ladder with.
        const rest = group.fields.filter((f) => !rungNames.has(f.name));
        return (
          <Group key={group.key} $wide={isCapability}>
            <GroupLabel>{group.label || "Assignment"}</GroupLabel>
            {isCapability && (
              <Ladder>
                {ladder.rungs.map((rung, i) => (
                  <Rung
                    key={rung.field.name}
                    rung={rung}
                    isTop={rung === ladder.top}
                    vehicle={vehicle}
                    now={now}
                    freshness={freshness}
                  />
                ))}
              </Ladder>
            )}
            {rest.length > 0 && (
              <Fields>
                {rest.map((field) => (
                  <Field
                    key={field.name}
                    field={field}
                    vehicle={vehicle}
                    now={now}
                    label={field.label}
                    freshness={freshness}
                  />
                ))}
              </Fields>
            )}
          </Group>
        );
      })}
    </Panel>
  );
};

/** One step of the ladder: rail, name, and what the approval actually says. */
const Rung: React.FC<{
  rung: CapabilityRung;
  isTop: boolean;
  vehicle: Vehicle;
  now: number;
  freshness: boolean;
}> = ({ rung, isTop, vehicle, now, freshness }) => {
  const fresh = freshnessFor(rung.field, vehicle.attrs, now);
  return (
    <RungRow $state={rung.state}>
      <RungName $top={isTop}>{rungLabel(rung.field)}</RungName>
      <RungValue>
        <ValueChip field={rung.field} value={rung.value} wrap />
        {freshness && <FieldFreshness field={rung.field} fresh={fresh} />}
      </RungValue>
    </RungRow>
  );
};

const Field: React.FC<{
  field: SchemaField;
  vehicle: Vehicle;
  now: number;
  label: string;
  freshness: boolean;
}> = ({ field, vehicle, now, label, freshness }) => {
  const value = vehicle.attrs[field.name] ?? null;
  const fresh = freshnessFor(field, vehicle.attrs, now);
  return (
    <Row>
      <Label>{label}</Label>
      <Value>
        <ValueChip
          field={field}
          value={value}
          plain={field.type === "text"}
          // Nothing here is competing for a single line, and the narrative is
          // the reason half these fields were moved off the table.
          wrap
        />
        {freshness && <FieldFreshness field={field} fresh={fresh} />}
      </Value>
    </Row>
  );
};

const Panel = styled.div`
  display: grid;
  /* Wider tracks than the fields strictly need. The panel was legible and still
     hard to read: four columns of tightly-packed label/value pairs is a wall,
     and the space between groups is what lets the eye find the group it wants. */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  align-content: start;
  gap: 20px 36px;
  padding: 18px 20px 20px;
`;

/**
 * One group, one track.
 *
 * Capability was briefly given two, on the theory that it is the densest block.
 * Drawn as a ladder it isn't dense any more, and spanning it only pushed the
 * last group onto a second line under a column of white — the groups read
 * better as four equal, complete columns than as a wide one plus an orphan.
 */
const Group = styled.div<{ $wide?: boolean }>`
  min-width: 0;
`;

const GroupLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.navy};
  padding-bottom: 7px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${tokens.line};
`;

const Ladder = styled.div`
  display: grid;
  gap: 2px;
  margin-bottom: 10px;
`;

/**
 * The rail — each segment coloured by what its own rung says.
 *
 * It ran green for every rung below the tractor's high-water mark, which on a
 * ragged ladder painted a refusal green: "Manual ✕ No" with a solid green bar
 * beside it, claiming a rung that had explicitly been denied. The hole is the
 * thing worth seeing, so the rail now shows it — green where an approval is
 * held, red where one is refused, amber where it is conditional. In the ordinary
 * bottom-up case the green still runs unbroken from the floor, which is the
 * "how far up" reading it was drawn for.
 *
 * Green rather than blue throughout: blue is this console's interaction colour
 * (hover, selection, focus), and a rung being held is a fact about the tractor
 * rather than something you are doing to it. It also matches the ✓ chips.
 */
const RAIL: Record<string, string> = {
  held: tokens.readyText,
  conditional: "rgba(255,150,0,0.75)",
  denied: "rgba(229,72,77,0.6)",
  off: tokens.line,
  unset: tokens.line,
};

const RungRow = styled.div<{ $state: string }>`
  display: grid;
  grid-template-columns: minmax(78px, 34%) 1fr;
  align-items: baseline;
  gap: 10px;
  padding: 4px 0 4px 11px;
  border-left: 3px ${(p) => (p.$state === "off" ? "dashed" : "solid")}
    ${(p) => RAIL[p.$state] ?? tokens.line};
`;

const RungName = styled.span<{ $top?: boolean }>`
  font-size: 12px;
  line-height: 1.35;
  font-weight: ${(p) => (p.$top ? 700 : 500)};
  color: ${(p) => (p.$top ? tokens.ink : tokens.slate)};
  overflow-wrap: anywhere;
`;

const RungValue = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Fields = styled.dl`
  margin: 0;
  display: grid;
  gap: 9px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
`;

/**
 * Wraps rather than truncates. A clipped label ("Most recent lidar calibrat…")
 * is a field you cannot identify, which defeats a panel whose whole job is to be
 * read. Rows stay comparable across expansions because every expansion renders
 * the same fields in the same order, so a wrapped label wraps identically in
 * each.
 */
const Label = styled.dt`
  flex: 0 0 40%;
  font-size: 12px;
  line-height: 1.35;
  color: ${tokens.slate};
  overflow-wrap: anywhere;
`;

const Value = styled.dd`
  margin: 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export default RowDetail;
